import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';

import { useAuth } from '@/hooks/useAuth';
import {
  fetchGetReceivedLeads,
  fetchGetSentLeads,
  fetchAcceptLead,
  fetchDeclineLead,
  fetchCloseLead,
} from '@/service/api';

type LeadViewType = 'received' | 'sent';

const getStatusBadge = (status?: string) => {
  const normalized = (status || '').toUpperCase();
  switch (normalized) {
    case 'PENDING':
    case 'NEW':
      return { label: '新线索', color: 'bg-amber-100 text-amber-800' };
    case 'ACCEPTED':
      return { label: '已接受', color: 'bg-green-100 text-green-700' };
    case 'DECLINED':
      return { label: '已拒绝', color: 'bg-red-50 text-red-600' };
    case 'CLOSED':
      return { label: '已关闭', color: 'bg-gray-100 text-gray-500' };
    default:
      return { label: status || '未知状态', color: 'bg-gray-100 text-gray-600' };
  }
};

export default function MyLeadsPage() {
  const [viewType, setViewType] = useState<LeadViewType>('received');
  const [activeStatus, setActiveStatus] = useState<string>('all');
  const [leads, setLeads] = useState<Api.Lead.LeadResponse[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const { isLoggedIn, user, rawUser, isEnterprise } = useAuth();
  const lastFetchKeyRef = useRef<string | null>(null);

  // 企业认证状态校验
  const certStatus = (rawUser?.certification_status || user?.verificationStatus || '').toUpperCase();
  const isEnterpriseCertified =
    isLoggedIn &&
    isEnterprise &&
    (certStatus === 'APPROVED' || certStatus === 'PASSED' || certStatus === 'SUCCESS' || user?.verified);

  const loadLeads = useCallback(async () => {
    if (viewType === 'sent' && !isEnterpriseCertified) {
      setLeads([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res =
        viewType === 'received'
          ? await fetchGetReceivedLeads({ page, page_size: 20 })
          : await fetchGetSentLeads({ page, page_size: 20 });
      setLeads(res.items || []);
      setTotal(res.total || 0);
    } catch (err: any) {
      console.error('获取线索列表失败:', err);
      setError(err instanceof Error ? err.message : '获取线索列表失败');
    } finally {
      setLoading(false);
    }
  }, [viewType, page, isEnterpriseCertified]);

  useEffect(() => {
    const fetchKey = `${viewType}-${page}-${isEnterpriseCertified}`;
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;
    loadLeads();
  }, [viewType, page, isEnterpriseCertified, loadLeads]);

  const handleTabChange = (type: LeadViewType) => {
    setViewType(type);
    setPage(1);
  };

  const handleAccept = async (id?: string) => {
    if (!id || actionLoadingId) return;
    setActionLoadingId(id);
    try {
      await fetchAcceptLead(id);
      await loadLeads();
    } catch (err: any) {
      alert(err instanceof Error ? err.message : '接受线索失败');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDecline = async (id?: string) => {
    if (!id || actionLoadingId) return;
    setActionLoadingId(id);
    try {
      await fetchDeclineLead(id);
      await loadLeads();
    } catch (err: any) {
      alert(err instanceof Error ? err.message : '拒绝线索失败');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleClose = async (id?: string) => {
    if (!id || actionLoadingId) return;
    setActionLoadingId(id);
    try {
      await fetchCloseLead(id);
      await loadLeads();
    } catch (err: any) {
      alert(err instanceof Error ? err.message : '关闭线索失败');
    } finally {
      setActionLoadingId(null);
    }
  };

  const filteredLeads = leads.filter((l) => {
    if (activeStatus === 'all') return true;
    return (l.status || '').toUpperCase() === activeStatus.toUpperCase();
  });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      <div className="pt-28 sm:pt-32">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading font-bold text-xl text-foreground-950">我的合作线索</h1>
              <p className="text-xs text-foreground-500 mt-1">管理与您发布或发送的合作与招聘线索</p>
            </div>
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-2 mb-6">
            <button
              onClick={() => handleTabChange('received')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${viewType === 'received'
                  ? 'bg-primary-500 text-foreground-950'
                  : 'text-foreground-600 hover:bg-gray-50'
                }`}
            >
              收到的线索
            </button>
            <button
              onClick={() => handleTabChange('sent')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${viewType === 'sent'
                  ? 'bg-primary-500 text-foreground-950'
                  : 'text-foreground-600 hover:bg-gray-50'
                }`}
            >
              发出的线索
              {!isEnterpriseCertified && (
                <span className="text-[10px] px-1.5 py-0.2 rounded bg-amber-400/20 text-amber-700 font-medium">
                  企业认证
                </span>
              )}
            </button>
          </div>

          {/* Status Filter */}
          {viewType === 'sent' && !isEnterpriseCertified ? null : (
            <div className="flex flex-wrap items-center gap-2 mb-6">
              {[
                { key: 'all', label: '全部状态' },
                { key: 'PENDING', label: '待处理' },
                { key: 'ACCEPTED', label: '已接受' },
                { key: 'DECLINED', label: '已拒绝' },
                { key: 'CLOSED', label: '已关闭' },
              ].map((s) => (
                <button
                  key={s.key}
                  onClick={() => setActiveStatus(s.key)}
                  className={`text-xs px-3 py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap ${activeStatus === s.key
                      ? 'bg-foreground-950 text-background-50'
                      : 'border border-background-300/60 text-foreground-500 hover:bg-gray-50'
                    }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}

          {/* Content Area */}
          {viewType === 'sent' && !isEnterpriseCertified ? (
            <div className="text-center py-16 px-4 rounded-2xl border border-gray-200/60 bg-white shadow-sm">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-50 flex items-center justify-center">
                <i className="ri-building-line text-3xl text-primary-600"></i>
              </div>
              <h2 className="font-heading font-bold text-lg text-foreground-950 mb-2">发出的线索仅限企业认证用户查看</h2>
              <p className="text-xs text-foreground-500 mb-6 max-w-md mx-auto leading-relaxed">
                根据平台服务规范，查看发出的合作与招聘线索详情仅对完成【企业认证】的账号开放。
              </p>
              {isLoggedIn ? (
                <Link
                  to="/certification"
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap shadow-sm"
                >
                  <i className="ri-shield-check-line"></i>
                  去完成企业认证
                </Link>
              ) : (
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to="/auth"
                    className="px-6 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap shadow-sm"
                  >
                    登录 / 注册
                  </Link>
                  <Link
                    to="/certification"
                    className="px-6 py-2.5 rounded-lg border border-primary-200 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-all cursor-pointer whitespace-nowrap"
                  >
                    企业认证
                  </Link>
                </div>
              )}
            </div>
          ) : loading ? (
            <div className="text-center py-20">
              <i className="ri-loader-4-line text-3xl text-primary-500 animate-spin mb-3"></i>
              <p className="text-sm text-foreground-500">正在加载合作线索...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
                <i className="ri-error-warning-line text-2xl text-red-500"></i>
              </div>
              <p className="text-sm text-red-600 mb-3">{error}</p>
              <button
                onClick={loadLeads}
                className="px-4 py-2 rounded-lg bg-primary-500 text-white text-xs font-medium hover:bg-primary-600 transition-all cursor-pointer"
              >
                重新加载
              </button>
            </div>
          ) : filteredLeads.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <i className={`${viewType === 'received' ? 'ri-inbox-line' : 'ri-send-plane-line'} text-2xl text-foreground-400`}></i>
              </div>
              <p className="text-sm text-foreground-500 mb-3">
                {viewType === 'received' ? '暂无收到的合作线索' : '暂无发出的合作线索'}
              </p>
              {viewType === 'sent' && (
                <Link
                  to="/talents"
                  className="text-sm text-primary-600 hover:text-primary-500 cursor-pointer font-medium"
                >
                  前往人才大厅寻找合作主体
                </Link>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredLeads.map((lead) => {
                const badge = getStatusBadge(lead.status);
                const statusUpper = (lead.status || '').toUpperCase();
                const isOperating = actionLoadingId === lead.id;

                return (
                  <div
                    key={lead.id}
                    className="glass-card rounded-xl p-5 border border-gray-200/60 hover:border-primary-200/50 transition-all bg-white"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono text-foreground-400">ID: {lead.id}</span>
                          <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${badge.color}`}>
                            {badge.label}
                          </span>
                          {lead.lead_type && (
                            <span className="text-[10px] px-2 py-0.5 rounded bg-primary-50 text-primary-600 font-medium">
                              {lead.lead_type === 'COOPERATION' ? '合作意向' : lead.lead_type === 'RECRUITMENT' ? '招聘需求' : lead.lead_type}
                            </span>
                          )}
                        </div>
                      </div>
                      {lead.created_at && (
                        <span className="text-xs text-foreground-400 whitespace-nowrap">
                          创建于 {new Date(lead.created_at).toLocaleString()}
                        </span>
                      )}
                    </div>

                    <div className="flex items-start gap-3 mb-4 p-4 rounded-xl bg-gray-50/80 border border-gray-100">
                      <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0 font-bold text-sm">
                        {lead.contact_name ? lead.contact_name.substring(0, 1) : '线'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-foreground-950">{lead.contact_name || '未提供名称'}</p>
                          <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-600">
                            {lead.contact_phone && (
                              <span className="flex items-center gap-1">
                                <i className="ri-phone-line text-emerald-600"></i>
                                {lead.contact_phone}
                              </span>
                            )}
                            {lead.contact_email && (
                              <span className="flex items-center gap-1">
                                <i className="ri-mail-line text-amber-600"></i>
                                {lead.contact_email}
                              </span>
                            )}
                            {lead.contact_wechat && (
                              <span className="flex items-center gap-1">
                                <i className="ri-wechat-line text-primary-600"></i>
                                {lead.contact_wechat}
                              </span>
                            )}
                          </div>
                        </div>
                        {lead.intention && (
                          <div className="mt-2 text-xs text-foreground-700 leading-relaxed bg-white p-3 rounded-lg border border-gray-100">
                            <p className="font-medium text-foreground-900 mb-1">意向说明：</p>
                            <p className="whitespace-pre-line">{lead.intention}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Timeline badges */}
                    <div className="flex flex-wrap items-center gap-3 text-[11px] text-foreground-400 mb-3">
                      {lead.accepted_at && <span>已于 {new Date(lead.accepted_at).toLocaleString()} 接受</span>}
                      {lead.declined_at && <span>已于 {new Date(lead.declined_at).toLocaleString()} 拒绝</span>}
                      {lead.closed_at && <span>已于 {new Date(lead.closed_at).toLocaleString()} 关闭</span>}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 justify-end pt-2 border-t border-gray-100">
                      {viewType === 'received' && statusUpper === 'PENDING' && (
                        <>
                          <button
                            onClick={() => handleAccept(lead.id)}
                            disabled={isOperating}
                            className="px-3.5 py-1.5 rounded-lg bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-1"
                          >
                            {isOperating && <i className="ri-loader-4-line animate-spin"></i>}
                            接受线索
                          </button>
                          <button
                            onClick={() => handleDecline(lead.id)}
                            disabled={isOperating}
                            className="px-3.5 py-1.5 rounded-lg border border-red-200 text-xs text-red-600 hover:bg-red-50 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-1"
                          >
                            {isOperating && <i className="ri-loader-4-line animate-spin"></i>}
                            拒绝
                          </button>
                        </>
                      )}

                      {statusUpper !== 'CLOSED' && (
                        <button
                          onClick={() => handleClose(lead.id)}
                          disabled={isOperating}
                          className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-foreground-500 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-1"
                        >
                          {isOperating && <i className="ri-loader-4-line animate-spin"></i>}
                          关闭线索
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
